import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Palette, Check, X, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const TEMPLATE_COLORS = {
  creative_1: 'from-blue-500 to-blue-700',
  creative_2: 'from-gray-100 to-gray-300',
  creative_3: 'from-red-500 to-orange-500',
  creative_4: 'from-green-500 to-emerald-600',
  creative_5: 'from-purple-500 to-pink-500',
  creative_6: 'from-blue-400 to-cyan-400',
  creative_7: 'from-gray-600 to-gray-800',
  creative_8: 'from-indigo-500 to-purple-600',
  creative_9: 'from-yellow-400 to-orange-400',
  creative_10: 'from-green-400 to-teal-500',
};

const AdminTemplatePage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await adminAPI.getTemplates();
      setTemplates(res.data.templates || []);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const handleToggleEnabled = async (templateId, currentEnabled) => {
    setSaving(prev => ({ ...prev, [templateId]: true }));
    try {
      await adminAPI.updateTemplate(templateId, { enabled: !currentEnabled });
      setTemplates(prev => prev.map(t => t.id === templateId ? { ...t, enabled: !currentEnabled } : t));
      toast.success(`Template ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update template'); }
    finally { setSaving(prev => ({ ...prev, [templateId]: false })); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  const enabledCount = templates.filter(t => t.enabled).length;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Creative Templates</h1>
          <p className="text-[#5F6368]">Manage creative templates — enable/disable for users</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm bg-[#E6F4EA] text-[#1E8E3E] px-3 py-1.5 rounded-lg font-medium">
            {enabledCount} / {templates.length} Enabled
          </span>
          <button onClick={fetchTemplates} className="btn-outline text-sm flex items-center gap-1" data-testid="refresh-templates">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Info box */}
      <div className="dashboard-card bg-[#E8F0FE] border-[#1A73E8]">
        <p className="text-sm text-[#1A73E8]">
          <strong>Template access is controlled per plan</strong> — Templates are numbered 1-10. Each plan grants access to templates up to its limit (Free=2, Basic=5, Pro=10). You can adjust these limits in <a href="/gmbadmin/membership" className="underline font-semibold">Membership Plans</a>. Disabling a template here hides it from all users regardless of plan.
        </p>
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-template-grid">
        {templates.map((tmpl) => {
          const gradient = TEMPLATE_COLORS[tmpl.id] || 'from-gray-400 to-gray-600';
          const isEnabled = tmpl.enabled !== false;
          const isSaving = saving[tmpl.id];

          return (
            <div
              key={tmpl.id}
              className={`dashboard-card flex flex-col ${!isEnabled ? 'opacity-60' : ''}`}
              data-testid={`admin-template-${tmpl.id}`}
            >
              {/* Preview + Order badge */}
              <div className={`h-24 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center relative mb-3`}>
                <span className="text-3xl font-bold text-white/60">{tmpl.order}</span>
                <span className="absolute top-2 left-2 text-[10px] bg-white/90 text-[#202124] px-1.5 py-0.5 rounded font-semibold">
                  #{tmpl.order}
                </span>
                <span className={`absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded font-semibold ${
                  isEnabled ? 'bg-[#E6F4EA] text-[#1E8E3E]' : 'bg-[#FCE8E6] text-[#D93025]'
                }`}>
                  {isEnabled ? 'Active' : 'Disabled'}
                </span>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h3 className="font-semibold text-[#202124]">{tmpl.name}</h3>
                <p className="text-xs text-[#5F6368] mt-0.5">{tmpl.description}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-[10px] bg-[#F1F3F4] text-[#5F6368] px-2 py-0.5 rounded">{tmpl.category}</span>
                  <span className="text-[10px] bg-[#F1F3F4] text-[#5F6368] px-2 py-0.5 rounded">Logo: {tmpl.layout?.logo_position}</span>
                </div>
              </div>

              {/* Plan Access Info */}
              <div className="mt-3 pt-3 border-t border-[#F1F3F4]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-[#5F6368]">
                    Available on: {tmpl.order <= 2 ? 'All plans' : tmpl.order <= 5 ? 'Basic & Pro' : 'Pro only'}
                  </p>
                </div>
                <button
                  onClick={() => handleToggleEnabled(tmpl.id, isEnabled)}
                  disabled={isSaving}
                  className={`w-full py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                    isEnabled
                      ? 'bg-[#FCE8E6] text-[#D93025] hover:bg-[#F8D7DA]'
                      : 'bg-[#E6F4EA] text-[#1E8E3E] hover:bg-[#D4EDDA]'
                  }`}
                  data-testid={`toggle-${tmpl.id}`}
                >
                  {isSaving ? (
                    <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                  ) : isEnabled ? (
                    <><X className="w-4 h-4" /> Disable Template</>
                  ) : (
                    <><Check className="w-4 h-4" /> Enable Template</>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminTemplatePage;
